<template>
    <div class="menu-page">
        <!-- Bloco: foto + dados -->
        <section-header
            title="Dados do perfil"
            icon="mdi-account-circle-outline"
        />

        <glass-panel
            radius="lg"
            class="pa-6 pa-md-8 mb-8"
        >
            <v-form @submit.prevent="save">
                <div class="profile-avatar-row mb-6">
                    <div class="profile-avatar">
                        <v-avatar size="120">
                            <v-img
                                v-if="userImage"
                                :src="userImage"
                            />
                            <div
                                v-else
                                class="profile-avatar__fallback"
                            >
                                <v-icon
                                    color="white"
                                    size="64"
                                >
                                    mdi-account
                                </v-icon>
                            </div>
                        </v-avatar>
                    </div>

                    <div class="profile-avatar-actions">
                        <div class="d-flex flex-wrap" style="gap:8px">
                            <v-btn
                                color="primary"
                                depressed
                                @click="pickFile"
                            >
                                <v-icon left>mdi-camera-outline</v-icon>
                                Alterar foto
                            </v-btn>
                            <v-btn
                                outlined
                                color="error"
                                @click="removeImage"
                            >
                                <v-icon left>mdi-trash-can-outline</v-icon>
                                Remover
                            </v-btn>
                        </div>
                        <p class="body-xs mt-3 mb-0">
                            Imagens permitidas: JPG, PNG. Tamanho máximo de 2 MB.
                        </p>
                    </div>
                </div>

                <info-notice
                    icon="mdi-robot-outline"
                    title="Informações para os assistentes de IA"
                    description="As informações abaixo poderão ser utilizadas pelos assistentes de IA para gerar conteúdo do seu blog e redes sociais."
                    class="mb-6"
                >
                    <template #actions>
                        <v-btn
                            small
                            outlined
                            color="primary"
                            :loading="saving"
                            :disabled="saving"
                            v-if="currentUser?.role === 'admin' || currentUser?.permissions?.includes('DEFAULT')"
                            @click="getFormInfo"
                        >
                            <v-icon
                                left
                                size="16"
                            >
                                mdi-clipboard-text-search-outline
                            </v-icon>
                            Usar do formulário
                        </v-btn>
                    </template>
                </info-notice>

                <v-row>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.name"
                            label="Nome do médico ou clínica"
                            required
                            outlined
                            hide-details="auto"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.email"
                            label="E-mail"
                            required
                            outlined
                            hide-details="auto"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.specialty"
                            label="Especialidades médicas"
                            required
                            outlined
                            hide-details="auto"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.target_audience"
                            label="Público alvo"
                            required
                            outlined
                            hide-details="auto"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.city"
                            label="Cidade"
                            required
                            outlined
                            hide-details="auto"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            v-model="user.state"
                            :items="stateOptions"
                            label="Estado"
                            required
                            outlined
                            hide-details="auto"
                            item-text="text"
                            item-value="value"
                            clearable
                        />
                    </v-col>
                    <v-col cols="12">
                        <v-radio-group
                            v-model="user.telemedicine"
                            label="Atende via telemedicina?"
                            required
                            row
                            hide-details="auto"
                            class="profile-telemedicine-group mt-0"
                        >
                            <v-radio label="Sim" :value="true" />
                            <v-radio label="Não" :value="false" />
                        </v-radio-group>
                    </v-col>
                    <v-col cols="12">
                        <v-textarea
                            v-model="user.professional_bio"
                            label="Bio profissional"
                            required
                            outlined
                            hide-details="auto"
                            rows="3"
                            auto-grow
                        />
                    </v-col>
                    <v-col cols="12">
                        <v-textarea
                            v-model="user.care_differentials"
                            label="Diferenciais de atendimento"
                            required
                            outlined
                            hide-details="auto"
                            rows="3"
                            auto-grow
                        />
                    </v-col>
                    <v-col cols="12">
                        <v-textarea
                            v-model="user.additional_information"
                            label="Outras informações relevantes"
                            required
                            outlined
                            hide-details="auto"
                            rows="3"
                            auto-grow
                        />
                    </v-col>

                    <v-col cols="12">
                        <div class="profile-quality">
                            <div class="d-flex align-center mb-3 flex-wrap" style="gap:8px">
                                <div class="mr-auto">
                                    <div class="card-title-strong" style="font-size:0.875rem">
                                        Qualidade das informações
                                    </div>
                                    <div class="body-xs">
                                        {{ profileQualityFilledCount }} de {{ profileQualityFields.length }} campos preenchidos
                                    </div>
                                </div>
                                <stat-badge :variant="profileQualityVariant">
                                    {{ profileQualityLabel }}
                                </stat-badge>
                            </div>
                            <v-progress-linear
                                :value="profileQualityPercent"
                                :color="profileQualityColor"
                                height="6"
                                rounded
                            />
                        </div>
                    </v-col>

                    <v-col cols="12" class="d-flex justify-end flex-wrap" style="gap:8px">
                        <v-btn
                            outlined
                            color="error"
                            :disabled="saving"
                            @click="load"
                        >
                            Cancelar
                        </v-btn>
                        <v-btn
                            color="primary"
                            depressed
                            type="submit"
                            :loading="saving"
                        >
                            <v-icon left>mdi-content-save-outline</v-icon>
                            Salvar alterações
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
        </glass-panel>

        <!-- Bloco: senha -->
        <section-header
            title="Redefinir senha"
            icon="mdi-lock-outline"
        />

        <glass-panel
            radius="lg"
            class="pa-6 pa-md-8"
        >
            <v-form @submit.prevent="updatePassword">
                <v-row>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="updatePasswordForm.oldPassword"
                            label="Senha atual"
                            outlined
                            hide-details="auto"
                            :type="updatePasswordForm.showPassword ? 'text' : 'password'"
                            :append-icon="updatePasswordForm.showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="updatePasswordForm.showPassword = !updatePasswordForm.showPassword"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="updatePasswordForm.newPassword"
                            label="Nova senha"
                            outlined
                            hide-details="auto"
                            :type="updatePasswordForm.showPassword ? 'text' : 'password'"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="updatePasswordForm.confirmNewPassword"
                            label="Confirmar nova senha"
                            outlined
                            hide-details="auto"
                            :type="updatePasswordForm.showPassword ? 'text' : 'password'"
                        />
                    </v-col>
                    <v-col cols="12" class="d-flex justify-end">
                        <v-btn
                            color="primary"
                            depressed
                            type="submit"
                        >
                            Redefinir senha
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
        </glass-panel>
    </div>
</template>

<script>
import Api from '@/lib/Api'
import { emitToastr } from '@/lib/Utils'

export default {
    name: 'PerfilView',

    data() {
        return {
            user: this.getDefaultUser(),
            saving: false,
            stateOptions: [
                { text: 'Acre', value: 'AC' },
                { text: 'Alagoas', value: 'AL' },
                { text: 'Amapá', value: 'AP' },
                { text: 'Amazonas', value: 'AM' },
                { text: 'Bahia', value: 'BA' },
                { text: 'Ceará', value: 'CE' },
                { text: 'Distrito Federal', value: 'DF' },
                { text: 'Espírito Santo', value: 'ES' },
                { text: 'Goiás', value: 'GO' },
                { text: 'Maranhão', value: 'MA' },
                { text: 'Mato Grosso', value: 'MT' },
                { text: 'Mato Grosso do Sul', value: 'MS' },
                { text: 'Minas Gerais', value: 'MG' },
                { text: 'Pará', value: 'PA' },
                { text: 'Paraíba', value: 'PB' },
                { text: 'Paraná', value: 'PR' },
                { text: 'Pernambuco', value: 'PE' },
                { text: 'Piauí', value: 'PI' },
                { text: 'Rio de Janeiro', value: 'RJ' },
                { text: 'Rio Grande do Norte', value: 'RN' },
                { text: 'Rio Grande do Sul', value: 'RS' },
                { text: 'Rondônia', value: 'RO' },
                { text: 'Roraima', value: 'RR' },
                { text: 'Santa Catarina', value: 'SC' },
                { text: 'São Paulo', value: 'SP' },
                { text: 'Sergipe', value: 'SE' },
                { text: 'Tocantins', value: 'TO' },
            ],
            image: {
                file: null,
                preview: null,
            },
            updatePasswordForm: {
                showPassword: false,
                oldPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            }
        }
    },
    computed: {
        userImage() {
            return this.image.preview || this.user.profile_picture
        },
        profileQualityFields() {
            return [
                'name',
                'email',
                'specialty',
                'target_audience',
                'city',
                'state',
                'telemedicine',
                'professional_bio',
                'care_differentials',
                'additional_information',
            ]
        },
        profileQualityFilledCount() {
            return this.profileQualityFields.filter(this.isProfileFieldFilled).length
        },
        profileQualityPercent() {
            return Math.round((this.profileQualityFilledCount / this.profileQualityFields.length) * 100)
        },
        profileQualityColor() {
            if (this.profileQualityPercent === 100) return 'success'
            if (this.profileQualityPercent >= 60) return 'warning'
            return 'error'
        },
        profileQualityVariant() {
            if (this.profileQualityPercent === 100) return 'positive'
            if (this.profileQualityPercent >= 60) return 'primary'
            return 'error'
        },
        profileQualityLabel() {
            if (this.profileQualityPercent === 100) return 'Muita qualidade'
            if (this.profileQualityPercent >= 60) return 'Qualidade parcial'
            return 'Pouca qualidade'
        },
        currentUser: {
            get() {
                return this.$store.state.currentUser
            },
            set(value) {
                this.$store.commit('setCurrentUser', value)
            }
        }
    },
    mounted() {
        this.load()

        this.$store.commit('setBreadcrumbs', [
            { label: 'Perfil' },
        ])
    },
    methods: {
        getDefaultUser() {
            return {
                name: '',
                email: '',
                profile_picture: '',
                specialty: '',
                target_audience: '',
                professional_bio: '',
                city: '',
                state: '',
                telemedicine: null,
                care_differentials: '',
                additional_information: '',
            }
        },
        isProfileFieldFilled(field) {
            const value = this.user?.[field]

            if (field === 'telemedicine') {
                return value === true || value === false
            }

            return !!String(value || '').trim()
        },
        getLatestAnswer(answers, questionIds) {
            const ids = Array.isArray(questionIds) ? questionIds : [questionIds]
            const answer = answers.find(x => ids.includes(x.question_id))
            const versions = answer?.versions || []

            if (!versions.length) return null

            return versions[versions.length - 1]?.value
        },
        normalizeTelemedicine(value) {
            if (value === true || value === false) return value
            if (typeof value !== 'string') return null

            const normalized = value.trim().toLowerCase()
            if (normalized === 'sim') return true
            if (['não', 'nao'].includes(normalized)) return false

            return null
        },
        normalizeState(value) {
            if (!value || typeof value !== 'string') return null

            const normalized = value.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
            const state = this.stateOptions.find(item => {
                return item.value.toLowerCase() === normalized || item.text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') === normalized
            })

            return state?.value || null
        },
        async load() {
            const user = await Api.getRemoteUser()

            this.user = {
                ...this.getDefaultUser(),
                ...user,
            }

            this.image.preview = null
            this.image.file = null
        },

        async getFormInfo() {
            this.saving = true
            const resp = await Api.getFormResponseByUserId(this.user._id)
            this.saving = false

            if (!resp.error && resp.message && resp.message.answers) {
                const answers = resp.message.answers
                const specialty = this.getLatestAnswer(answers, 'expertise')
                const professional_bio = this.getLatestAnswer(answers, 'about')
                const city = this.getLatestAnswer(answers, ['city', 'location'])
                const state = this.normalizeState(this.getLatestAnswer(answers, 'state'))
                const telemedicine = this.normalizeTelemedicine(this.getLatestAnswer(answers, 'accept_telemedicine'))
                const differentials = [
                    this.getLatestAnswer(answers, 'differences1'),
                    this.getLatestAnswer(answers, 'differences2'),
                    this.getLatestAnswer(answers, 'differences3'),
                ].filter(Boolean).join('\n\n')

                if (specialty) this.user.specialty = specialty
                if (professional_bio) this.user.professional_bio = professional_bio
                if (city) this.user.city = city
                if (state) this.user.state = state
                if (telemedicine !== null) this.user.telemedicine = telemedicine
                if (differentials) this.user.care_differentials = differentials
            }
        },

        async pickFile() {
            const input = document.createElement('input')

            input.type = 'file'
            input.accept = 'image/jpeg, image/png'

            input.onchange = async (e) => {
                const file = e.target.files[0]
                this.setImage(file)
            }

            input.click()
        },
        removeImage() {
            this.image.file = null
            this.user.profile_picture = null
        },
        setImage(file) {
            const preview = URL.createObjectURL(file)
            this.image.file = file
            this.image.preview = preview
        },
        async saveUserImage(file) {
            const response = await Api.uploadProfilePicture(file)

            if (response.error) {
                emitToastr('error', response.message)
                return
            }

            this.user.profile_picture = response.message
        },
        async save() {
            this.saving = true

            if (this.image.file) {
                await this.saveUserImage(this.image.file)
            }

            const data = Object.fromEntries(Object.keys(this.getDefaultUser()).map(key => [key, this.user[key]]))
            const response = await this.$api.updateUser({ id: this.currentUser._id, data })

            if (response.error) {
                emitToastr('error', response.message)
                return
            }

            this.currentUser.name = this.user.name
            this.currentUser.email = this.user.email
            this.currentUser.profile_picture = this.user.profile_picture
            this.currentUser.specialty = this.user.specialty
            this.currentUser.target_audience = this.user.target_audience
            this.currentUser.professional_bio = this.user.professional_bio
            this.currentUser.city = this.user.city
            this.currentUser.state = this.user.state
            this.currentUser.telemedicine = this.user.telemedicine
            this.currentUser.care_differentials = this.user.care_differentials
            this.currentUser.additional_information = this.user.additional_information

            setTimeout(() => {
                this.saving = false
                emitToastr('success', 'Dados atualizados com sucesso')
            }, 800)
        },
        async updatePassword() {
            const { oldPassword, newPassword, confirmNewPassword } = this.updatePasswordForm

            if (!oldPassword || !newPassword || !confirmNewPassword) {
                emitToastr('error', 'Preencha todos os campos')
                return
            }

            if (newPassword !== confirmNewPassword) {
                emitToastr('error', 'As senhas não conferem')
                return
            }

            const response = await Api.changeUserPassword(newPassword, oldPassword)

            if (response.error) {
                return
            }

            emitToastr('success', 'Senha alterada com sucesso')
        }
    },
}
</script>

<style scoped>
.profile-avatar-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
}

.profile-avatar {
    border-radius: 9999px;
    padding: 4px;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dim) 100%);
    box-shadow: var(--shadow-pulse);
}

.profile-avatar__fallback {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dim) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
}

.profile-avatar-actions {
    flex: 1;
    min-width: 0;
}

.profile-quality {
    padding: 16px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--glass-panel-border);
    background: var(--glass-soft-bg);
}

@media (max-width: 599px) {
    .profile-telemedicine-group >>> legend.v-label {
        flex: 0 0 100%;
        display: block;
        width: 100%;
        margin-bottom: 10px;
        line-height: 1.4;
        white-space: normal;
    }

    .profile-telemedicine-group >>> .v-input--radio-group__input {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        column-gap: 22px;
        row-gap: 6px;
    }

    .profile-telemedicine-group >>> .v-radio {
        margin-right: 0;
        margin-bottom: 0;
    }
}

@media (min-width: 600px) {
    .profile-avatar-row {
        flex-direction: row;
        align-items: center;
        text-align: left;
        gap: 24px;
    }
}
</style>
